import React, { Component } from "react";
import cookie from "react-cookies";
import axios from "axios";
import { Container, Row, Col, Form, FormGroup, Label, Input, Button, Alert, Spinner, Card, CardBody } from "reactstrap";
import constants from "../../Constants";
import { Redirect } from "react-router-dom";

const baseURL = constants.baseURL;

// QikTruck branding colors
const brandColors = {
  primary: "#4f6367",
  secondary: "#7a9e9f",
  accent: "#b8d8d8",
  light: "#eef5db",
  dark: "#2d3c40",
  success: "#4caf50",
  danger: "#f44336"
};

const customStyles = {
  header: {
    backgroundColor: brandColors.primary,
    color: "white",
    borderBottom: "none",
    padding: "1.5rem",
    borderTopLeftRadius: "8px",
    borderTopRightRadius: "8px",
  },
  formCard: {
    borderRadius: "8px",
    boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
    border: "none",
    overflow: "hidden",
  },
  cardBody: {
    padding: "2rem",
    backgroundColor: "#fff",
  },
  inputField: {
    borderRadius: "4px",
    borderColor: "#ddd",
    padding: "10px 15px",
    fontSize: "16px",
    transition: "border-color 0.3s",
  },
  submitButton: {
    backgroundColor: brandColors.primary,
    borderColor: brandColors.primary,
    padding: "12px",
    fontWeight: "600",
    textTransform: "uppercase",
    letterSpacing: "1px",
    borderRadius: "4px",
    marginTop: "1.5rem",
    transition: "background-color 0.3s",
    maxWidth: "300px",
    margin: "0 auto",
  },
  formGroup: {
    marginBottom: "1.5rem",
  },
  label: {
    fontWeight: "600",
    marginBottom: "0.5rem",
    color: brandColors.dark,
  },
  subtitle: {
    color: "#666",
    marginBottom: "1.5rem",
    fontSize: "16px",
    lineHeight: "1.5",
    maxWidth: "450px",
    margin: "0 auto",
  },
  alert: {
    borderRadius: "4px",
    marginBottom: "1.5rem",
  },
  pageTitle: {
    fontSize: "28px", 
    fontWeight: "bold",
    color: brandColors.primary,
    textAlign: "center",
    marginBottom: "1rem"
  }
};

class ProfileCompletion extends Component {
  constructor(props) {
    super(props);
    this.state = {
      profileData: {
        firstname: "",
        lastname: "",
        email: "",
        mobilenumber: ""
      },
      isSubmitting: false,
      isLoading: true,
      errorMessage: "",
      successMessage: "",
      redirectToDashboard: false
    };
  }

  componentDidMount() {
    document.title = "Complete Your Profile | QikTruck";
    // Check if the profile is already complete
    const ctName = cookie.load("ctName");
    if (ctName) {
      this.setState({ redirectToDashboard: true });
    } else {
      // Fetch existing profile data if available
      this.fetchCustomerProfile();
    }
  }

  fetchCustomerProfile = () => {
    const sessionId = cookie.load("session-id-qt");
    
    this.setState({ isLoading: true });
    
    if (!sessionId) {
      this.setState({ isLoading: false });
      return; // No session, can't fetch profile
    }
    
    axios
      .get(baseURL + "/customer-profile", {
        headers: {
          "Content-Type": "application/json",
          Authorization: sessionId,
        },
      })
      .then((response) => {
        if (response.status === 200 && response.data) {
          // Update form with existing profile data if available
          const { email, firstname, lastname, mobilenumber } = response.data;
          
          this.setState({
            profileData: {
              firstname: firstname || "",
              lastname: lastname || "",
              email: email || "",
              mobilenumber: mobilenumber || ""
            },
            isLoading: false
          });
        } else {
          this.setState({ isLoading: false });
        }
      })
      .catch((error) => {
        console.log("Error fetching profile data:", error);
        this.setState({ isLoading: false });
        // Don't show error to user, just use empty form
      });
  };

  handleInputChange = (event) => {
    const { name, value } = event.target;
    this.setState({
      profileData: {
        ...this.state.profileData,
        [name]: value
      }
    });
  };

  validateForm = () => {
    const { firstname, lastname, email, mobilenumber } = this.state.profileData;
    if (!firstname || !lastname || !email || !mobilenumber) {
      this.setState({ errorMessage: "All fields are required" });
      return false;
    }
    
    // Simple email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      this.setState({ errorMessage: "Please enter a valid email address" });
      return false;
    }
    
    // Simple phone validation (at least 10 digits)
    const phoneRegex = /^\d{10,}$/;
    if (!phoneRegex.test(mobilenumber.replace(/[^0-9]/g, ''))) {
      this.setState({ errorMessage: "Please enter a valid phone number (at least 10 digits)" });
      return false;
    }
    
    return true;
  };

  submitProfileData = (e) => {
    e.preventDefault();
    
    if (!this.validateForm()) {
      return;
    }
    
    this.setState({ isSubmitting: true, errorMessage: "", successMessage: "" });
    
    axios
      .post(baseURL + "/customer-profile-update", this.state.profileData, {
        headers: {
          "Content-Type": "application/json",
          Authorization: cookie.load("session-id-qt"),
        },
      })
      .then((response) => {
        if (response.status === 200) {
          // Set the cookie
          cookie.save("ctName", this.state.profileData.firstname, {
            path: "/",
            maxAge: 86400,
            secure: true,
          });
          
          this.setState({
            isSubmitting: false,
            successMessage: "Profile updated successfully!",
          });
          
          // Redirect to dashboard after a short delay
          setTimeout(() => {
            this.setState({ redirectToDashboard: true });
          }, 1500);
        }
      })
      .catch((error) => {
        console.error("Error updating profile:", error);
        this.setState({
          isSubmitting: false,
          errorMessage: "Failed to update profile. Please try again."
        });
      });
  };

  render() {
    const { profileData, isSubmitting, isLoading, errorMessage, successMessage, redirectToDashboard } = this.state;
    
    if (redirectToDashboard) {
      return <Redirect to="/dashboard" />;
    }
    
    return (
      <div className="profile-completion-page" style={{ backgroundColor: "#f8f9fa", minHeight: "100vh" }}>
        <Container className="py-5">
          <Row className="justify-content-center">
            <Col xs={12} sm={10} md={8} lg={6}>
              <div className="text-center mb-4">
                <img 
                  src="https://media.qiktruck.co.za/qiktruck-new-logo-bg.png" 
                  alt="QikTruck Logo" 
                  height="150"
                  className="mb-4"
                />
                <h1 style={customStyles.pageTitle}>Complete Your Profile</h1>
              </div>
              
              <Card style={customStyles.formCard}>
                <div style={customStyles.header}>
                  <div className="text-center">
                    <i className="mdi mdi-account-circle mr-2" style={{ fontSize: "24px" }}></i>
                    <span style={{ fontSize: "18px" }}>Profile Information</span>
                  </div>
                </div>
                <CardBody style={customStyles.cardBody}>
                  <p style={customStyles.subtitle}>
                    Please provide your information to continue using QikTruck services.
                  </p>
                  
                  {errorMessage && (
                    <Alert color="danger" style={customStyles.alert}>
                      <i className="mdi mdi-alert-circle mr-2"></i>
                      {errorMessage}
                    </Alert>
                  )}
                  
                  {successMessage && (
                    <Alert color="success" style={customStyles.alert}>
                      <i className="mdi mdi-check-circle mr-2"></i>
                      {successMessage}
                    </Alert>
                  )}
                  
                  {isLoading ? (
                    <div className="text-center py-5">
                      <Spinner color="primary" size="lg" />
                      <p className="mt-3" style={{ color: "#777" }}>Loading your profile data...</p>
                    </div>
                  ) : (
                    <Form onSubmit={this.submitProfileData}>
                      <Row className="g-3">
                        <Col xs={12} md={6} className="mb-3">
                          <FormGroup style={customStyles.formGroup}>
                            <Label for="firstname" style={customStyles.label}>
                              First Name
                            </Label>
                            <Input
                              id="firstname"
                              name="firstname"
                              placeholder="Enter your first name"
                              type="text"
                              value={profileData.firstname}
                              onChange={this.handleInputChange}
                              required
                              style={customStyles.inputField}
                            />
                          </FormGroup>
                        </Col>
                        <Col xs={12} md={6} className="mb-3">
                          <FormGroup style={customStyles.formGroup}>
                            <Label for="lastname" style={customStyles.label}>
                              Last Name
                            </Label>
                            <Input
                              id="lastname"
                              name="lastname"
                              placeholder="Enter your last name"
                              type="text"
                              value={profileData.lastname}
                              onChange={this.handleInputChange}
                              required
                              style={customStyles.inputField}
                            />
                          </FormGroup>
                        </Col>
                      </Row>
                      <Row className="g-3">
                        <Col xs={12} className="mb-3">
                          <FormGroup style={customStyles.formGroup}>
                            <Label for="email" style={customStyles.label}>
                              Email Address
                            </Label>
                            <Input
                              id="email"
                              name="email"
                              placeholder="Enter your email"
                              type="email"
                              value={profileData.email}
                              onChange={this.handleInputChange}
                              required
                              style={customStyles.inputField}
                            />
                          </FormGroup>
                        </Col>
                        <Col xs={12} className="mb-3">
                          <FormGroup style={customStyles.formGroup}>
                            <Label for="mobilenumber" style={customStyles.label}>
                              Mobile Number
                            </Label>
                            <Input
                              id="mobilenumber"
                              name="mobilenumber"
                              placeholder="Enter your mobile number"
                              type="tel"
                              value={profileData.mobilenumber}
                              onChange={this.handleInputChange}
                              required
                              style={customStyles.inputField}
                            />
                          </FormGroup>
                        </Col>
                      </Row>
                      <div className="text-center mt-2">
                        <Button 
                          color="primary" 
                          type="submit" 
                          disabled={isSubmitting}
                          style={customStyles.submitButton}
                          className="btn-shadow"
                          block
                        >
                          {isSubmitting ? (
                            <>
                              <Spinner size="sm" className="mr-2" /> 
                              Saving...
                            </>
                          ) : (
                            <>
                              <i className="mdi mdi-check-circle mr-2"></i>
                              Save Profile
                            </>
                          )}
                        </Button>
                      </div>
                      <div className="text-center mt-3">
                        <small style={{ color: "#999", fontSize: "11px" }}>
                          By submitting, you agree to QikTruck's Terms of Service.
                        </small>
                      </div>
                    </Form>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    );
  }
}

export default ProfileCompletion; 